import { useState } from 'react'
import { Plus, Trash2, GripVertical, X } from 'lucide-react'
import type { UnderwritingModel, UnderwritingField, UnderwritingFieldType } from '@/types/underwritingModels'

interface Props {
  model: UnderwritingModel
  onAddField: (sectionId: string, field: UnderwritingField) => void
  onRemoveField: (sectionId: string, fieldId: string) => void
  onClose: () => void
}

const FIELD_TYPES: UnderwritingFieldType[] = ['currency', 'percentage', 'number', 'text', 'select', 'calculated']

export default function ModelEditorPanel({ model, onAddField, onRemoveField, onClose }: Props) {
  const [addingTo, setAddingTo] = useState<string | null>(null)
  const [newField, setNewField] = useState({
    id: '',
    label: '',
    type: 'currency' as UnderwritingFieldType,
    required: false,
  })

  const handleAdd = () => {
    if (!addingTo || !newField.id || !newField.label) return
    onAddField(addingTo, {
      id: newField.id.toLowerCase().replace(/\s+/g, '_'),
      label: newField.label,
      type: newField.type,
      section: addingTo,
      required: newField.required,
      assetClassSpecific: true,
    })
    setNewField({ id: '', label: '', type: 'currency', required: false })
    setAddingTo(null)
  }

  if (model.locked) {
    return (
      <div className="rounded-lg border border-red-400/20 bg-red-400/[0.05] p-4 text-center">
        <p className="text-[12px] text-red-300">Model is locked. Unlock to edit.</p>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-amber-400/15 bg-black/40 backdrop-blur-xl">
      <div className="flex items-center justify-between border-b border-amber-400/10 px-4 py-2.5">
        <div>
          <h3 className="text-[13px] font-semibold text-stone-100">Model Editor</h3>
          <p className="text-[10px] text-stone-500">{model.assetClass} &mdash; {model.sections.reduce((n, s) => n + s.fields.length, 0)} fields</p>
        </div>
        <button onClick={onClose} className="rounded p-1 text-stone-500 hover:text-stone-300 transition-colors">
          <X size={14} />
        </button>
      </div>

      <div className="max-h-[500px] overflow-y-auto p-3 space-y-3">
        {model.sections.map((section) => (
          <div key={section.id} className="rounded border border-stone-700/40 bg-stone-900/30">
            <div className="flex items-center justify-between px-3 py-1.5 border-b border-stone-700/30">
              <span className="text-[11px] font-medium text-stone-300">{section.label}</span>
              <button
                onClick={() => setAddingTo(addingTo === section.id ? null : section.id)}
                className="flex items-center gap-1 text-[10px] text-amber-400/70 hover:text-amber-300 transition-colors"
              >
                <Plus size={10} />
                Add Field
              </button>
            </div>

            <div className="px-2 py-1.5 space-y-0.5">
              {section.fields.map((field) => (
                <div key={field.id} className="flex items-center gap-2 rounded px-2 py-1 hover:bg-white/[0.02] group">
                  <GripVertical size={10} className="text-stone-700 flex-shrink-0" />
                  <span className="flex-1 text-[11px] text-stone-300 truncate">{field.label}</span>
                  <span className="rounded bg-stone-800 px-1.5 py-0.5 text-[8px] font-mono text-stone-500">{field.type}</span>
                  {field.required && (
                    <span className="text-[8px] text-amber-400/50">REQ</span>
                  )}
                  {field.assetClassSpecific && (
                    <button
                      onClick={() => onRemoveField(section.id, field.id)}
                      className="opacity-0 group-hover:opacity-100 rounded p-0.5 text-red-400/60 hover:text-red-400 transition-all"
                      title="Remove field"
                    >
                      <Trash2 size={10} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {addingTo === section.id && (
              <div className="border-t border-stone-700/30 p-2.5 space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <input
                    placeholder="Field ID (e.g. new_metric)"
                    value={newField.id}
                    onChange={(e) => setNewField({ ...newField, id: e.target.value })}
                    className="rounded border border-stone-700/50 bg-stone-900/60 px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/30 focus:outline-none"
                  />
                  <input
                    placeholder="Label"
                    value={newField.label}
                    onChange={(e) => setNewField({ ...newField, label: e.target.value })}
                    className="rounded border border-stone-700/50 bg-stone-900/60 px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/30 focus:outline-none"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={newField.type}
                    onChange={(e) => setNewField({ ...newField, type: e.target.value as UnderwritingFieldType })}
                    className="rounded border border-stone-700/50 bg-stone-900/60 px-2 py-1 text-[11px] text-stone-200 focus:border-amber-400/30 focus:outline-none"
                  >
                    {FIELD_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-[10px] text-stone-400">
                    <input
                      type="checkbox"
                      checked={newField.required}
                      onChange={(e) => setNewField({ ...newField, required: e.target.checked })}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button
                    onClick={handleAdd}
                    disabled={!newField.id || !newField.label}
                    className="ml-auto rounded bg-amber-400/15 px-3 py-1 text-[10px] font-medium text-amber-300 hover:bg-amber-400/25 disabled:opacity-30 transition-colors"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
